import {CloseOutlined, EditOutlined, EnvironmentOutlined, IdcardOutlined, LogoutOutlined, MailOutlined, PhoneOutlined, SaveOutlined, UserOutlined} from '@ant-design/icons';
import {Alert, Avatar, Button, Divider, Form, Input, Spin, Tag} from 'antd';
import {jwtDecode} from 'jwt-decode';
import {useEffect, useRef, useState} from 'react';
import toast from 'react-hot-toast';
import {useDispatch, useSelector} from 'react-redux';
import {useNavigate} from 'react-router-dom';

import {RQUpdateProfileAPI, RqEmployeeLookupById} from '../../../reactQuery/apis';
import {logoutStart} from '../../../redux/authAction';
import {selectAuth} from '../../../redux/selectors';

const ROLE_CONFIG = {
  ADMIN: {
    label: 'Administrator',
    color: 'gold',
  },
  SUPERVISOR: {
    label: 'Supervisor',
    color: 'purple',
  },
  ACCOUNTANT: {
    label: 'Accountant',
    color: 'cyan',
  },
};

const STATUS_COLOR = {
  ACTIVE: 'green',
  INACTIVE: 'red',
  PENDING: 'orange',
};

const ProfileScreen = () => {
  const [isEditing, setIsEditing] = useState(false);
  const skipRedirectRef = useRef(false);
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [form] = Form.useForm();
  const auth = useSelector(selectAuth);
  const {loggedIn} = useSelector(state => state.auth);
  const token = auth?.tokenData;

  let decodedToken = null;
  let id = null;
  let role = null;

  if (token) {
    try {
      decodedToken = jwtDecode(token);
      id = decodedToken?.userId;
      role = decodedToken?.role;
    } catch {
      decodedToken = null;
    }
  }

  const handleLogout = () => {
    skipRedirectRef.current = true;
    dispatch(logoutStart());
    navigate('/');
  };

  const {
    data: profileDetails,
    isLoading,
    refetch,
    error,
  } = RqEmployeeLookupById(id, token, {
    enabled: !!id && !!token,   // ← removed loggedIn gate
    staleTime: 1000 * 60 * 5,
    retry: 2,
  });

  const {mutate: updateProfile} = RQUpdateProfileAPI({
    onSuccess: response => {
      if (response) {
        refetch();
        setIsEditing(false);
        toast.success('Profile updated successfully!');
      } else {
        toast.error('Something went wrong!');
      }
    },
    onError: err => {
      toast.error(err?.message || 'Update failed');
    },
  });

  useEffect(() => {
    if (profileDetails) {
      form.setFieldsValue(profileDetails);
    }
  }, [profileDetails, form]);

  const onFinish = values => {
    updateProfile({token, formData: values, id, role});
  };

  const handleCancel = () => {
    form.setFieldsValue(profileDetails);
    setIsEditing(false);
  };

  // Show spinner while token is present but data not yet loaded
  if (isLoading) {
    return (
      <div className={'flex justify-center items-center h-screen'}>
        <Spin size={'large'} />
      </div>
    );
  }

  // Only hard-redirect when there's genuinely no session
  if (!loggedIn && !token) {
    navigate('/');
    return null;
  }

  if (error) {
    return (
      <div className={'flex justify-center items-center h-screen p-4'}>
        <Alert
          message={'Failed to load profile'}
          description={'Unable to fetch your profile data. Please try again later.'}
          type={'error'}
          showIcon
        />
      </div>
    );
  }

  const roleConfig = ROLE_CONFIG[role] || ROLE_CONFIG['USER'];
  const initials = `${profileDetails?.firstName?.[0] ?? ''}${profileDetails?.lastName?.[0] ?? ''}`.toUpperCase();
  const fullName = `${profileDetails?.firstName ?? ''} ${profileDetails?.lastName ?? ''}`.trim();

  return (
    <div className={'relative min-h-screen bg-gray-50'}>
      <div className={'max-w-5xl mx-auto md:px-4 py-8 lg:py-12'}>

        {/* Header Banner */}
        <div className={'bg-primary-900 rounded-md p-6 mb-6 flex flex-col sm:flex-row items-center sm:items-end gap-4 text-white'}>
          <Avatar
            size={80}
            className={'bg-white text-primary-800 text-2xl font-bold flex-shrink-0'}
            style={{color: '#1e3a5f', fontWeight: 700}}
          >
            {initials || <UserOutlined />}
          </Avatar>
          <div className={'flex-1 text-center sm:text-left'}>
            <h1 className={'text-2xl font-bold text-white capitalize m-0'}>{fullName || 'Your Profile'}</h1>
            <p className={'text-primary-200 text-sm mt-1 m-0'}>{profileDetails?.email}</p>
            <div className={'flex flex-wrap justify-center sm:justify-start gap-2 mt-2'}>
              <Tag color={roleConfig.color} className={'capitalize'}>
                {roleConfig.label}
              </Tag>
              {profileDetails?.status && (
                <Tag color={STATUS_COLOR[profileDetails.status] ?? 'default'}>
                  {profileDetails.status}
                </Tag>
              )}
            </div>
          </div>
          <Button
            danger
            icon={<LogoutOutlined />}
            onClick={handleLogout}
            className={'bg-red-500 hover:!bg-red-600 border-red-500 hover:!border-red-600 text-white hover:!text-white flex-shrink-0'}
          >
            Log Out
          </Button>
        </div>

        {/* Main Card */}
        <div className={'bg-primary-50 rounded-md shadow-sm border border-primary-200 overflow-hidden'}>

          {/* Card Header */}
          <div className={'flex md:flex-row flex-col items-center justify-between px-6 py-4 border-b border-gray-100'}>
            <div className={'flex items-center gap-2'}>
              <IdcardOutlined className={'text-primary-700 text-lg'} />
              <span className={'header'}>Personal Information</span>
            </div>
            {!isEditing ? (
              <Button
                icon={<EditOutlined />}
                onClick={() => setIsEditing(true)}
                className={'btn-primary'}
              >
                Edit Profile
              </Button>
            ) : (
              <div className={'flex gap-2'}>
                <Button icon={<CloseOutlined />} onClick={handleCancel}>
                  Cancel
                </Button>
                <Button
                  type={'primary'}
                  icon={<SaveOutlined />}
                  onClick={() => form.submit()}
                  className={'btn-primary py-4'} 
                >
                  Save Changes
                </Button>
              </div>
            )}
          </div>

          {/* Form */}
          <div className={'p-6'}>
            <Form form={form} layout={'vertical'} onFinish={onFinish}>

              {/* Personal Details Section */}
              <div className={'mb-2'}>
                <p className={'text-xs font-semibold text-primary-400 uppercase tracking-wider mb-3 flex items-center gap-1'}>
                  <UserOutlined /> Personal Details
                </p>
                <div className={'grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-1'}>
                  <Form.Item
                    name={'firstName'}
                    label={'First Name'}
                    rules={[
                      {required: true, message: 'First name is required'},
                      {whitespace: true, message: 'Cannot be blank'},
                      {pattern: /^[A-Za-z]+$/, message: 'Only letters allowed'},
                    ]}
                  >
                    <Input className={'!text-primary-700'} disabled={!isEditing} placeholder={'First name'} />
                  </Form.Item>

                  <Form.Item
                    name={'lastName'}
                    label={'Last Name'}
                    rules={[
                      {required: true, message: 'Last name is required'},
                      {whitespace: true, message: 'Cannot be blank'},
                      {pattern: /^[A-Za-z]+$/, message: 'Only letters allowed'},
                    ]}
                  >
                    <Input className={'!text-primary-700'} disabled={!isEditing} placeholder={'Last name'} />
                  </Form.Item>
                </div>
              </div>

              <Divider className={'my-4'} />

              {/* Contact Section */}
              <div className={'mb-2'}>
                <p className={'text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3 flex items-center gap-1'}>
                  <PhoneOutlined /> Contact
                </p>
                <div className={'grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-1'}>
                  <Form.Item
                    name={'mobile'}
                    label={'Mobile'}
                    rules={[
                      {required: true, message: 'Enter contact number'},
                      {pattern: /^[0-9]{10}$/, message: 'Must be exactly 10 digits'},
                    ]}
                  >
                    <Input
                      className={'!text-primary-700'} 
                      disabled={!isEditing}
                      placeholder={'10-digit mobile number'}
                      maxLength={10}
                      prefix={<PhoneOutlined className={'text-gray-300'} />}
                    />
                  </Form.Item>

                  <Form.Item
                    name={'email'}
                    label={'Email'}
                    rules={[
                      {required: true, message: 'Email is required'},
                      {type: 'email', message: 'Invalid email format'},
                    ]}
                  >
                    {/* Email is always read-only */}
                    <Input
                      disabled
                      className={'!text-primary-700'} 
                      placeholder={'Email address'}
                      prefix={<MailOutlined className={'text-gray-300'} />}
                    />
                  </Form.Item>
                </div>
              </div>

              <Divider className={'my-4'} />

              {/* Address Section */}
              <div className={'mb-2'}>
                <p className={'text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3 flex items-center gap-1'}>
                  <EnvironmentOutlined /> Address
                </p>
                <div className={'grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-1'}>
                  <Form.Item
                    name={'street'}
                    label={'Street'}
                    rules={[
                      {required: true, message: 'Street is required'},
                      {whitespace: true, message: 'Cannot be blank'},
                    ]}
                  >
                    <Input className={'!text-primary-700'} disabled={!isEditing} placeholder={'Street'} />
                  </Form.Item>

                  <Form.Item
                    name={'apartment'}
                    label={'Apartment / Suite'}
                    rules={[
                      {required: true, message: 'Apartment is required'},
                      {whitespace: true, message: 'Cannot be blank'},
                    ]}
                  >
                    <Input className={'!text-primary-700'}  disabled={!isEditing} placeholder={'Apartment or suite number'} />
                  </Form.Item>

                  <Form.Item
                    name={'city'}
                    label={'City'}
                    rules={[
                      {required: true, message: 'City is required'},
                      {
                        pattern: /^[A-Za-z]+(?: [A-Za-z]+)*$/,
                        message: 'Only letters allowed',
                      },
                    ]}
                  >
                    <Input className={'!text-primary-700'} disabled={!isEditing} placeholder={'City'} />
                  </Form.Item>

                  <Form.Item
                    name={'district'}
                    label={'District'}
                    rules={[
                      {required: true, message: 'District is required'},
                      {whitespace: true, message: 'Cannot be blank'},
                    ]}
                  >
                    <Input className={'!text-primary-700'} disabled={!isEditing} placeholder={'District'} />
                  </Form.Item>

                  <Form.Item
                    name={'state'}
                    label={'State'}
                    rules={[
                      {required: true, message: 'State is required'},
                      {pattern: /^[A-Za-z\s]+$/, message: 'Only letters allowed'},
                    ]}
                  >
                    <Input className={'!text-primary-700'} disabled={!isEditing} placeholder={'State'} />
                  </Form.Item>

                  <Form.Item
                    name={'pinCode'}
                    label={'Pin Code'}
                    rules={[
                      {required: true, message: 'Pin code is required'},
                      {pattern: /^[0-9]{6}$/, message: 'Must be exactly 6 digits'},
                    ]}
                  >
                    <Input className={'!text-primary-700'} disabled={!isEditing} placeholder={'6-digit pin code'} maxLength={6} />
                  </Form.Item>

                  <Form.Item
                    name={'address'}
                    label={'Full Address'}
                    className={'md:col-span-2'}
                    rules={[
                      {required: true, message: 'Address is required'},
                      {whitespace: true, message: 'Cannot be blank'},
                    ]}
                  >
                    <Input.TextArea
                      disabled={!isEditing}
                      className={'!text-primary-700'} 
                      rows={3}
                      placeholder={'Full address'}
                    />
                  </Form.Item>
                </div>
              </div>

              {/* Admin-only: read-only info */}
              {role === 'EMPLOYEE' && profileDetails?.createdOn && (
                <>
                  <Divider className={'my-4'} />
                  <div>
                 
                    <div className={'grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-1'}>
                      <div className={'text-sm text-gray-500'}>
                        <span className={'text-gray-400'}>Member since: </span>
                        {new Date(profileDetails.createdOn).toLocaleDateString('en-IN', {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric',
                        })}
                      </div>
                      <div className={'text-sm text-gray-500'}>
                        <span className={'text-gray-400'}>Verified: </span>
                        {profileDetails.verifcation ? (
                          <Tag color={'green'}>Yes</Tag>
                        ) : (
                          <Tag color={'orange'}>Pending</Tag>
                        )}
                      </div>
                    </div>
                  </div>
                </>
              )}
            </Form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProfileScreen;
